/**
 * Générateur — Attendu 194 — O4 « Il gère le déclenchement d'un script en
 * réponse à un événement. »
 * Niveau : 4ème | Thème #12 Algorithmique (O4, Niveau 2) | Session A.
 * Source prof : O4 (Créé n°22 « qui déclenche quoi ? ») — les blocs-chapeaux
 * événementiels de Scratch.
 *
 * ADAPTATIONS scoring : le doc relie 4 blocs-chapeaux à 4 situations → relier
 * (paires tirées d'un pool + paramètres concrets : touche, n° de fond) pour la
 * variété. Les questions b/c → QCM concrets (touche/déclencheur tirés).
 *
 * 3 cartes :
 *  1. declencheurs — relier chaque bloc-chapeau à sa situation (relier).
 *  2. frise_touche — comment lancer un script déclenché par une touche ? (QCM).
 *  3. scripts_indep — deux scripts, deux déclencheurs, indépendants ? (QCM).
 */

import {
    randInt, randomChoice, shuffle, randomChoiceMultiple, makeQcm, makeRelier,
} from '../../../utils';

const ATTENDU_ID = 194;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_DECL = 'Relier chaque déclencheur à sa situation';
const L_FRISE = 'Lancer un script déclenché par une touche';
const L_INDEP = 'Deux déclencheurs indépendants';

const TOUCHES = ['espace', 'flèche droite', 'flèche haut', 'a', 'b'];
const PRENOMS = ['Léna', 'Youri', 'Sacha', 'Nina', 'Timéo', 'Awa', 'Malo', 'Jade'];

// ═════════ Carte 1 — relier déclencheur ↔ situation ═════════

const genDeclencheurs = () => {
    const touche = randomChoice(TOUCHES);
    const niveau = randInt(2, 4);
    // pool de paires événement ↔ situation (chacune sémantiquement distincte)
    const pool = [
        { source: 'quand le drapeau vert est cliqué', target: 'lancer le jeu au démarrage' },
        { source: `quand la touche ${touche} est pressée`, target: 'faire sauter le personnage pendant la partie' },
        { source: 'quand ce lutin est cliqué', target: 'afficher un message quand on clique sur le personnage' },
        { source: `quand le fond devient Niveau ${niveau}`, target: 'changer la musique au changement de décor' },
        { source: 'quand je reçois le message « perdu »', target: 'afficher l\'écran de fin de partie' },
        { source: 'quand le chronomètre dépasse 30', target: 'arrêter la partie après 30 secondes' },
    ];
    const pairs = shuffle(randomChoiceMultiple(pool, 4));
    const texte = '<p><b>Relie chaque bloc-chapeau (l\'événement qui déclenche le script) '
        + 'à la situation qui lui correspond.</b></p>';
    const payload = makeRelier(pairs, texte, opts(L_DECL));
    payload._meta.params = { touche, niveau };
    return payload;
};

// ═════════ Carte 2 — lancer un script sur touche (QCM) ═════════

const genFriseTouche = () => {
    const touche = randomChoice(TOUCHES);
    const prenom = randomChoice(PRENOMS);
    const correct = `Appuyer sur la touche ${touche}.`;
    const choix = [
        correct,
        'Cliquer sur le drapeau vert.',
        'Cliquer sur le lutin.',
        'Changer le fond de la scène.',
    ];
    const texte = `<p><b>Dans le projet de ${prenom}, le script de la frise commence par le bloc `
        + `« quand la touche ${touche} est pressée ». Que doit faire l'utilisateur pour lancer `
        + 'ce script ?</b></p>';
    const payload = makeQcm(choix, correct, texte, opts(L_FRISE));
    payload._meta.params = { touche, prenom };
    return payload;
};

// ═════════ Carte 3 — deux déclencheurs indépendants (QCM) ═════════

const genScriptsIndep = () => {
    const touche = randomChoice(TOUCHES);
    const prenom = randomChoice(PRENOMS);
    const correct = 'Oui : chaque script se déclenche par son propre événement, indépendamment de l\'autre.';
    const choix = [
        correct,
        'Non : un projet ne peut contenir qu\'un seul script à la fois.',
        'Non : le second script attend forcément la fin du premier.',
        `Non : il faut d'abord cliquer le drapeau vert pour que la touche ${touche} fonctionne.`,
    ];
    const texte = `<p><b>Dans le projet de ${prenom}, un script commence par « quand le drapeau vert `
        + `est cliqué », un autre par « quand la touche ${touche} est pressée ». `
        + 'Peuvent-ils s\'exécuter indépendamment l\'un de l\'autre ?</b></p>';
    const payload = makeQcm(choix, correct, texte, opts(L_INDEP));
    payload._meta.params = { touche, prenom };
    return payload;
};

// ═════════ Assemblage ═════════

const GENERATORS = {
    declencheurs_relier: genDeclencheurs,
    frise_touche_qcm_unique: genFriseTouche,
    scripts_indep_qcm_unique: genScriptsIndep,
};

const CARTES = [
    { id: 'declencheurs', label: L_DECL },
    { id: 'frise_touche', label: L_FRISE },
    { id: 'scripts_indep', label: L_INDEP },
];

export const CATEGORIES = CARTES.map((c) => ({
    id: c.id,
    label: c.label,
    formatsList: Object.keys(GENERATORS).filter((k) => k.startsWith(`${c.id}_`))
        .map((k) => ({ key: k, label: c.label })),
}));

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
